/**
 * polishRace.js — F-415-POLISH-RESUME. The dual-version polish race, extracted so it can be tested.
 *
 * WHY THIS FILE EXISTS. The assembler leg was made resumable on 2026-08-08 because API Gateway cuts
 * the connection at 29s while the Lambda keeps writing (2026-08-07 at 32.7s and 2026-08-08 at 44.3s
 * both threw away a story that had actually been written). The POLISH leg was not: it raced a Flick
 * and a Whole against a 25s client timer chosen as "~4s margin under the 29s ceiling", so it never
 * reached a 504 — it killed the call first and reported `pair_timeout`. Honest, but still a lost
 * story, and the coverage fix makes it certain rather than occasional: the Whole polish measures
 * 31.3s–35.2s with the uncapped length clause, which is past the old timer on every run.
 *
 * Extracted rather than left inline because App.js cannot be loaded in Jest (react-pdf ESM), so a
 * decision inlined there cannot have a real unit test. Same reason generationTimeout.js and
 * saveGuards.js exist. See .claude/rules/app-multidomain.md.
 *
 * WHAT DID NOT CHANGE. The verdict still belongs to `chooseTimeoutOutcome` (F-334, founder ruling:
 * COMPLETE OR FAIL, no substitutes). A Flick-only landing is still refused. A finished Whole is
 * still kept even when its partner never arrives. This file answers WHEN the race ends; that one
 * answers WHAT THE TELLER GETS, and it stays where it is.
 *
 * WHAT DID CHANGE, AND WHY IT IS NOT SCOPE CREEP. Raising the budget past 29s forces a second
 * change, because the old 25s timer was accidentally SHIELDING the race from a Flick failure. Today
 * both calls sit inside a `Promise.all`, so a Flick that rejects rejects the pair — but at 25s the
 * timer almost always fired first and the pair landed Whole-only. Move the budget past the 29s cut
 * and that shield is gone: a Flick cut by the gateway at 29s would now reject and take down a Whole
 * that was still coming. So Flick failures are ABSORBED here (the Flick is the optional version;
 * losing it costs the version chooser, not the story). The Whole's own failures are untouched and
 * still propagate — a 400 or a 429 must never be reported as a timeout.
 *
 * WHY ONLY THE WHOLE IS RESUMABLE. The Whole is the version the teller keeps and the one that runs
 * long (Sonnet, full prompt). The Flick is Haiku and fast. Making both resumable would double the
 * job-store writes on every generation to protect the version that is discarded when the pair is
 * incomplete anyway.
 *
 * F-416 — WHY THE WHOLE'S ERRORS NO LONGER PROPAGATE. They used to, deliberately, so that a 429 or a
 * 400 could never be reported as a timeout. That half was right and is unchanged. What was wrong is
 * where they landed: straight past the call site into App.js's catch-all (`:5766`), which names every
 * error it receives `assembler_fail` — so a polish failure paged the founder wearing the name of the
 * leg that had already SUCCEEDED. They are now caught here and classified by LEG, which is the thing
 * only this file knows. The cause enum's job is to say what broke; a cause that names the wrong leg
 * is worse than no cause, because it is acted on.
 */

import { chooseTimeoutOutcome } from './generationTimeout';
import { RESUME_BUDGET_MS } from './aiClient';

/**
 * How long the pair may take before the generation fails honestly.
 *
 * It MUST sit above `RESUME_BUDGET_MS`, or it preempts the very resume it exists to wait for and the
 * resume becomes decorative — the old bug wearing a bigger number. The resume budget is where the
 * evidence put it (the worst observed generation was 44.3s; 120s is ~2.7x that), and the resume
 * mechanism is the thing that actually knows whether the Lambda is still working. A wall clock
 * cannot tell "still generating" from "hung", so it is a backstop here, not the decision-maker.
 */
export const POLISH_PAIR_BUDGET_MS = RESUME_BUDGET_MS + 5000;

/** A cancellable delay. Cancellation matters: an uncancelled 125s timer would outlive the page. */
const defaultTimer = (ms) => {
  let handle;
  const p = new Promise((resolve) => { handle = setTimeout(resolve, ms); });
  p.cancel = () => clearTimeout(handle);
  return p;
};

const TIMED_OUT = Symbol('polish-pair-budget');
const WHOLE_FAILED = Symbol('polish-whole-failed');

/**
 * Which polish failure this was — F-416.
 *
 * The two resume errors mean the Lambda's work was lost after the 29s gateway cut: either the job
 * row was gone by the time we polled (the Lambda hit its 120s ceiling and the store called the row a
 * corpse at 135s) or it was still running when the resume budget ran out. Both are the LONG-STORY
 * failure, and they are the ones a founder reads as "the ceiling is still too low".
 *
 * Anything else is the API refusing us — a 429, a 400, a dead network — which is a different problem
 * with a different fix, and must never be reported as a wait. Kept as two causes rather than three
 * on purpose: telling GENERATION_JOB_MISSING from GENERATION_RESUME_TIMEOUT in the alert is
 * F-415-ALERT-IS-UNDIAGNOSABLE's job, not this one.
 */
export function classifyPolishFailure(err) {
  const message = err && err.message;
  if (message === 'GENERATION_JOB_MISSING' || message === 'GENERATION_RESUME_TIMEOUT') {
    return 'polish_timeout';
  }
  return 'polish_fail';
}

/**
 * Run the Flick + Whole polish pair and decide what the teller gets.
 *
 * @param {object} args
 * @param {() => Promise<{text?: string}>} args.callWhole  starts the Whole call (resumable at the call site)
 * @param {() => Promise<{text?: string}>} args.callFlick  starts the Flick call
 * @param {number} [args.budgetMs]  overall ceiling; defaults to POLISH_PAIR_BUDGET_MS
 * @param {(ms: number) => Promise<void>} [args.timer]  injectable delay (tests drive virtual time)
 * @returns {Promise<{status: 'complete', wholeData: object, flickData: object|null}
 *                 | {status: 'failed', cause: string, wholeData: null, flickData: object|null, error?: Error}>}
 *   Never throws (F-416). A Whole that fails returns `status: 'failed'` with a cause naming the
 *   POLISH leg and the original error attached, rather than escaping to a catch-all that would
 *   rename it after the assembler.
 */
export async function runPolishPair({ callWhole, callFlick, budgetMs = POLISH_PAIR_BUDGET_MS, timer = defaultTimer }) {
  // Captured as soon as each settles, independent of how the race ends. F-334 Fix 2: before this was
  // held, a Whole that came back while Flick was still out was DISCARDED and the teller was bounced
  // to the conversation with no story ([V-334] A2 red).
  let wholeResult = null;
  let flickResult = null;

  // Started SYNCHRONOUSLY, exactly as the call site fired them before the extraction: both requests
  // are in flight in the same tick, so total latency stays ~= one generation rather than two.
  const start = (fn) => { try { return Promise.resolve(fn()); } catch (err) { return Promise.reject(err); } };

  const wholeCall = start(callWhole);
  let wholeError = null;
  const wholePromise = wholeCall.then(
    (data) => { wholeResult = data; return data; },
    // F-416: captured, not propagated. Classified below, by leg.
    (err) => { wholeError = err; return null; },
  );

  // F-416: a Whole that FAILED ends the race immediately. Its partner cannot rescue the generation
  // (a Flick alone is refused — F-334), so waiting for a hung Flick would turn a 3s failure into a
  // 155s one for the teller. Never wins the race when the Whole succeeds: that branch never settles.
  const wholeFailedFast = wholeCall.then(() => new Promise(() => {}), () => WHOLE_FAILED);

  // Absorbed, not raced: see the header. A failed Flick costs the version chooser, never the story.
  const flickPromise = start(callFlick).then(
    (data) => { flickResult = data; return data; },
    (err) => { console.warn('[F-415] Flick polish failed; landing Whole-only:', err && err.message); return null; },
  );

  const pair = Promise.all([wholePromise, flickPromise]);
  // Both legs now absorb their own rejection, so this cannot fire today. Kept deliberately: a future
  // edit that stops absorbing one of them should not surface as an unhandled rejection instead.
  pair.catch(() => {});

  const budget = timer(budgetMs);
  const settled = await Promise.race([pair, wholeFailedFast, Promise.resolve(budget).then(() => TIMED_OUT)]);
  if (budget && typeof budget.cancel === 'function') budget.cancel();

  // F-416: checked FIRST, and before the budget, because a real error is a more specific answer than
  // "the clock ran out" — and because a Whole that errored at 3s must not be reported as a timeout.
  if (wholeError) {
    return {
      status: 'failed',
      cause: classifyPolishFailure(wholeError),
      wholeData: null,
      flickData: flickResult,
      error: wholeError, // the original, unchanged, so the console line keeps the real reason
    };
  }

  if (settled !== TIMED_OUT) {
    // Both settled inside the budget. The Whole is still put through the same verdict as the timeout
    // path, so a call that came back carrying nothing fails as `empty_output` rather than reaching
    // the reveal as a story (PIPE-OUT-1's intent, extended to the reveal by F-334 2c).
    const done = chooseTimeoutOutcome({ whole: wholeResult, flick: flickResult });
    if (done.outcome !== 'whole') {
      return { status: 'failed', cause: done.cause || 'pair_timeout', wholeData: null, flickData: flickResult };
    }
    return { status: 'complete', wholeData: wholeResult, flickData: flickResult };
  }

  const outcome = chooseTimeoutOutcome({ whole: wholeResult, flick: flickResult });
  if (outcome.outcome !== 'whole') {
    return { status: 'failed', cause: outcome.cause || 'pair_timeout', wholeData: null, flickData: flickResult };
  }
  // One version only; the chooser stays hidden (versions.flick === null).
  return { status: 'complete', wholeData: wholeResult, flickData: null };
}
